import {
  type CSSProperties,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  Button,
  GradientText,
  Layout,
  SuccessIcon,
  SupportIcon,
  Title,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { LegalModal } from "../components/legal/LegalModal";
import { Popup } from "../components/Popup";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useClaimLink } from "../hooks/useClaimLink";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useWatchedOperation } from "../hooks/useWatchedOperation";
import { cancelOperation, fetchOperationDocument } from "../services/api";
import { formatHeroAmount } from "../utils";
import { CheckIcon, CopyIcon, LimitInfoIcon, StatusCircleIcon, StatusFailedIcon, StatusPendingIcon } from "../assets/icons";
import {
  resolveStatusToneColor,
  toStatusPresentation,
  type StatusTone,
} from "../utils/operationStatus";
import {
  shouldOfferNativeShare,
  shareOrDownloadBlob,
} from "../utils/shareReceipt";
import { buildReceiptPdf } from "../utils/receiptPdf";
import {
  buildReceiptRows,
  hidesReceiptSettlementCopy,
  referenceLabel,
  splitVisibleReferences,
} from "../utils/receiptRows";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { RequireActiveClerkSession } from "../hooks/useRequireActiveClerkSession";
import { getEvents, safeCapture } from "../analytics";
import "../styles/legal.style.css";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";


/**
 * Corridor document kinds used only to pick a live PDF for share.
 *
 * TODO(documents): surface documents[] beyond this share shortcut. The same
 * EXECUTION_ENRICHED envelope that carries references also describes
 * PAYOUT_RECEIPT / SENDER_RECEIPT / PREPAYMENT_DISCLOSURE /
 * COMPLIANCE_STATEMENT. We should later show availability === "PENDING"
 * ("receipt on the way") and a dedicated download list for AVAILABLE kinds.
 * See raas-widgets-backend/docs/corridor-facade/execution-enrichment.md
 * §Campos de documents[].
 */
const RECEIPT_KINDS = ["SENDER_RECEIPT", "PAYOUT_RECEIPT"] as const;

const statusIcon = (tone: StatusTone, color: string) => {
  switch (tone) {
    case "completed":
      return <SuccessIcon size={10} colors={[color, color]} />;
    case "failed":
    case "expired":
      return <StatusFailedIcon size={10} color={color} />;
    case "initial":
      return <StatusPendingIcon size={10} color={color} />;
    case "progress":
    default:
      return <StatusCircleIcon size={10} color={color} />;
  }
};

/**
 * Receipt / detail screen for a single operation.
 *
 * Reached from the transactions list (TransactionsPage) by tapping a row; the
 * operation ID travels in the path (`/receipt-page/:operationId`). The layout
 * mirrors the screenbook `receipt-summary` gallery screen, populated with the
 * real operation fetched from the backend.
 */
export const ReceiptSummaryPage = memo(() => {
  const { operationId } = useParams<{ operationId: string }>();
  return (
    <RequireActiveClerkSession
      resume={
        operationId
          ? { kind: "receipt", operationId }
          : { kind: "transactions" }
      }
    >
      <ReceiptSummaryPageContent />
    </RequireActiveClerkSession>
  );
});

const ReceiptSummaryPageContent = memo(() => {
  useClaimLink();
  const { t, i18n } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { redirectTo } = useRouteWithToken();
  const { operationId } = useParams<{ operationId: string }>();

  const colorConfig = theme.whiteView;
  const {
    bodyCopy,
    dropzoneColors,
    title,
    footerColor,
    background,
    backgroundBtn,
    textColorBtn,
  } = colorConfig;
  const { operation, status, refresh } = useWatchedOperation(
    session.data?.linkId,
    operationId
  );
  const loading = status === "loading";
  const [showTransactionPolicyModal, setShowTransactionPolicyModal] =
    useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelResult, setCancelResult] = useState<"success" | "error" | null>(
    null
  );
  const [sharing, setSharing] = useState(false);
  const [copiedType, setCopiedType] = useState<string | null>(null);
  const sharingRef = useRef(false);
  const offerNativeShare = useMemo(
    () => shouldOfferNativeShare("application/pdf"),
    []
  );
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? "en").split("-")[0];

  const canCancel = operation?.cancellation?.allowed === true;

  useEffect(() => {
    if (!operation?.cancellation?.allowed || !operation.cancellation.expiresAt) {
      return;
    }
    const ms = new Date(operation.cancellation.expiresAt).getTime() - Date.now();
    if (ms <= 0) return;
    const id = window.setTimeout(() => {
      void refresh();
    }, ms);
    return () => window.clearTimeout(id);
  }, [operation?.cancellation?.allowed, operation?.cancellation?.expiresAt, refresh]);

  const handleCancelConfirm = useCallback(async () => {
    const linkId = session.data?.linkId;
    if (!linkId || !operationId) return;
    setCancelling(true);
    setCancelResult(null);
    try {
      const result = await cancelOperation(linkId, operationId);
      if (result.mode === "request") {
        redirectTo(APP_ROUTES.CANCELLATION_IN_PROGRESS_PAGE, { replace: true });
        return;
      }
      await refresh();
      setCancelResult("success");
    } catch {
      redirectTo(
        `${APP_ROUTES.CANCELLATION_FAILED_PAGE}/${operationId}`,
        { replace: true },
      );
    } finally {
      setCancelling(false);
      setShowConfirm(false);
    }
  }, [session.data?.linkId, operationId, refresh, redirectTo]);

  const handleCopyReference = useCallback(
    async (type: string, value: string) => {
      try {
        await navigator.clipboard.writeText(value);
        setCopiedType(type);
        window.setTimeout(() => {
          setCopiedType((current) => (current === type ? null : current));
        }, 2000);
        safeCapture(getEvents().PAYOUT_CODE_COPY, {
          operation_id: operationId,
          reference_type: type,
        });
      } catch {
        // Clipboard may be unavailable (insecure context / denied permission).
      }
    },
    [operationId]
  );

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("done"), {
      operation_id: operationId,
    });
  }, [operationId]);

  const handleShare = async () => {
    if (sharingRef.current || !operation) return;
    sharingRef.current = true;
    setSharing(true);
    const fileId = operationId ?? "recibo";
    const title = t("receipt.shareTitle");
    try {
      const receiptKind = RECEIPT_KINDS.find((kind) =>
        operation.documents?.some(
          (doc) => doc.kind === kind && doc.availability === "AVAILABLE"
        )
      );
      const linkId = session.data?.linkId;

      let result: Awaited<ReturnType<typeof shareOrDownloadBlob>> | "skipped" =
        "skipped";
      if (receiptKind && linkId && operationId) {
        try {
          const blob = await fetchOperationDocument(
            linkId,
            operationId,
            receiptKind
          );
          result = await shareOrDownloadBlob({
            blob,
            fileName: t("receipt.shareFileName", { id: fileId }),
            title,
            mediaType: "application/pdf",
          });
        } catch {
          result = "skipped";
        }
      }

      if (result === "skipped") {
        const { prominent } = splitVisibleReferences(operation.references);
        const blob = await buildReceiptPdf({
          operation,
          rows: buildReceiptRows(operation, t, { lang }),
          prominentRefs: prominent,
          partnerName: session.data?.partner?.name,
          strings: {
            documentTitle: t("receipt.documentTitle"),
            youTransferred: t("receipt.youTransferred"),
            transactionId: t("receipt.transactionId"),
            footerNote: hidesReceiptSettlementCopy(operation.status)
              ? ""
              : t("receipt.footerNote"),
          },
          t,
          lang,
        });
        result = await shareOrDownloadBlob({
          blob,
          fileName: t("receipt.shareFileName", { id: fileId }),
          title,
          mediaType: "application/pdf",
        });
      }

      if (result === "shared" || result === "downloaded") {
        safeCapture(getEvents().SHARE_CLICK, {
          source: "receipt_card",
          method: result === "shared" ? "native" : "download",
          asset: "pdf",
        });
      }
    } finally {
      sharingRef.current = false;
      setSharing(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <section className="receipt-summary-loading">
          <span
            className="loader-spinner"
            style={
              {
                "--spinner-start": bodyCopy,
                "--spinner-end": bodyCopy,
              } as CSSProperties
            }
          />
        </section>
      );
    }

    if (!operation) {
      return (
        <section className="receipt-summary-loading">
          <BodyCopy textColor={bodyCopy}>{t("receipt.notFound")}</BodyCopy>
        </section>
      );
    }

    const source = operation.sourceCurrency;
    const { tone } = toStatusPresentation(operation.status);
    const statusColor = resolveStatusToneColor(tone, backgroundBtn);
    const { prefix, suffix, intPart: sendInt, decPart: sendDec } = formatHeroAmount(
      operation.sendAmount,
      source
    );
    const { prominent: prominentRefs } = splitVisibleReferences(
      operation.references
    );
    const rows = buildReceiptRows(operation, t, { lang });

    return (
      <section className="receipt-summary-container">
        <section className="receipt-summary-scroll">
          <ScrollFadeIndicator color={bodyCopy} />
          <section className="summary-section">
            <section className="receipt-card-capture">
              <section className="summary-title">
                <BodyCopy textColor={bodyCopy}>{t("receipt.youTransferred")}</BodyCopy>
                <GradientText as="h1">
                  {prefix}{sendInt}.<label>{sendDec}</label>
                  {suffix ? (
                    <span className="receipt-hero-currency">{suffix}</span>
                  ) : null}
                </GradientText>
                <BodyCopy
                  className="receipt-trx-caption"
                  textColor={bodyCopy}
                  aria-label={t("receipt.transactionId")}
                >
                  {operation.operationId.slice(0, 8).toUpperCase()}
                </BodyCopy>
              </section>
              {/*
               * Canonical references from EXECUTION_ENRICHED. Additive: they
               * do not move the FSM and may arrive late over SSE
               * (useWatchedOperation upserts the document).
               *
               * TODO(documents): documents[] is the pending companion of the
               * same envelope — see RECEIPT_KINDS and
               * raas-widgets-backend/docs/corridor-facade/execution-enrichment.md
               * §Campos de documents[].
               */}
              {prominentRefs.map((ref) => (
                <section
                  key={`${ref.type}:${ref.value}`}
                  className="receipt-reference-hero"
                  style={{ borderColor: "#eeeeee" }}
                >
                  <BodyCopy textColor={bodyCopy}>
                    {referenceLabel(ref.type, ref.label, t, lang)}
                  </BodyCopy>
                  <div className="receipt-reference-value-wrap">
                    <p
                      className="receipt-reference-value"
                      style={{ color: TITLE_COLOR }}
                    >
                      {ref.value}
                    </p>
                    <button
                      type="button"
                      className="receipt-reference-copy"
                      style={{ color: bodyCopy }}
                      aria-label={
                        copiedType === ref.type
                          ? t("receipt.copied")
                          : t("receipt.copy")
                      }
                      onClick={() =>
                        void handleCopyReference(ref.type, ref.value)
                      }
                    >
                      {copiedType === ref.type ? (
                        <CheckIcon size={16} />
                      ) : (
                        <CopyIcon size={16} />
                      )}
                    </button>
                  </div>
                </section>
              ))}
              <section className="summary-info">
                {rows.map((row) => (
                  <SummaryInfoItem
                    key={row.id}
                    status={row.label}
                    value={row.value}
                    isBold={row.emphasis}
                    statusColor={row.id === "status" ? statusColor : undefined}
                    icon={
                      row.id === "status"
                        ? statusIcon(tone, statusColor)
                        : undefined
                    }
                  />
                ))}
              </section>
            </section>
          </section>
          <section className="receipt-summary-disclaimers">
            {!hidesReceiptSettlementCopy(operation.status) && (
              <BodyCopy className="footer-text" textColor={footerColor}>
                {t("receipt.footerNote")}
              </BodyCopy>
            )}
            <BodyCopy className="footer-text">
              <GradientText
                as="button"
                type="button"
                className="receipt-cancel-policy"
                textColor={title}
                onClick={() => setShowTransactionPolicyModal(true)}
              >
                {t("receipt.cancelationPolicy")}
              </GradientText>
            </BodyCopy>
          </section>
        </section>
      </section>
    );
  };

  const { openSupport, supportSheet } = useSupportSheet();

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="need-help-header receipt-summary-section">
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            onBackClick={() => redirectTo(APP_ROUTES.TRANSACTIONS_PAGE)}
            onIconClick={() => openSupport()}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="receipt-summary-content">
          {renderContent()}
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section transactions-footer">
            {canCancel && !cancelResult && (
              <BodyCopy className="footer-text">
                <button
                  type="button"
                  className="receipt-cancel-transfer"
                  style={{ color: TITLE_COLOR }}
                  onClick={() => setShowConfirm(true)}
                >
                  {t("receipt.cancelTransfer")}
                </button>
              </BodyCopy>
            )}
            {operation && (
            <section className="receipt-share-cta">
              {cancelResult === "success" && (
                <BodyCopy textColor={bodyCopy} className="cancel-feedback">
                  {t("receipt.cancelSuccess")}
                </BodyCopy>
              )}
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                onClick={() => void handleShare()}
                disabled={sharing}
              >
                {offerNativeShare
                  ? t("receipt.shareReceipt")
                  : t("receipt.downloadReceipt")}
              </Button>
            </section>
            )}
          </section>
        </Layout.Footer>
      </AuraLayout>
      <LegalModal
        doc="transactionPolicy"
        visible={showTransactionPolicyModal}
        onClose={() => setShowTransactionPolicyModal(false)}
      />
      <Popup visible={showConfirm} onClose={() => setShowConfirm(false)} showClose={false}>
        <section className="cancel-confirm-slot">
          <LimitInfoIcon
            className="cancel-confirm-icon"
            size={60}
            colors={[TITLE_COLOR, TITLE_COLOR]}
          />
          <Title title={t("receipt.cancelConfirmTitle")} align="center" color={TITLE_COLOR} />
          <BodyCopy textColor={bodyCopy}>
            {t("receipt.cancelConfirmMessageRequest")}
          </BodyCopy>
          <div className="cancel-confirm-actions">
            <Button
              size="large"
              variant="outline"
              background={backgroundBtn}
              color={TITLE_COLOR}
              style={{ backgroundColor: background }}
              onClick={() => void handleCancelConfirm()}
              disabled={cancelling}
            >
              {cancelling ? "..." : t("receipt.cancelConfirmYes")}
            </Button>
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={() => setShowConfirm(false)}
              disabled={cancelling}
            >
              {t("receipt.cancelConfirmNo")}
            </Button>
          </div>
        </section>
      </Popup>
      {supportSheet}
    </>
  );
});

interface SummaryInfoItemProps {
  isBold?: boolean;
  statusColor?: string;
  status: string;
  value: string | React.ReactNode;
  icon?: React.ReactNode;
}

const SummaryInfoItem: React.FC<SummaryInfoItemProps> = ({
  isBold,
  status,
  value,
  statusColor,
  icon,
}) => (
  <section className={`summary-info-item ${isBold ? "bold" : ""} `}>
    <p>{status}</p>
    <p
      className={icon ? "summary-info-status" : undefined}
      style={{
        ...(statusColor ? { color: statusColor } : undefined),
        whiteSpace: "pre-line",
        textAlign: "right",
      }}
    >
      {icon}
      {value}
    </p>
  </section>
);
